
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const normalize = (str) =>
  str?.toLowerCase().replace(/[^a-z0-9]/gi, "").trim();

const ExportDataGraph = ({ selectedProduct: propSelectedProduct }) => {
  const { data: exportData } = useSelector(
    (state) => state.exportDataReducer || { data: null }
  );

  const selectedProduct = useMemo(() => {
    if (propSelectedProduct) return propSelectedProduct;
    if (!exportData) return null;

    for (const [category, categoryData] of Object.entries(exportData)) {
      if (category === "_summary") continue;

      for (const [commodity] of Object.entries(categoryData)) {
        if (commodity === "_summary") continue;
        return commodity.includes("rice") && commodity.includes("basmoti")
          ? "RICE BASMOTI"
          : commodity;
      }
    }
    return null;
  }, [propSelectedProduct, exportData]);

  const chartData = useMemo(() => {
    if (!selectedProduct || !exportData) return [];

    const countryMap = {};
    Object.entries(exportData).forEach(([category, categoryData]) => {
      if (category === "_summary") return;

      Object.entries(categoryData).forEach(([commodity, commodityData]) => {
        if (commodity === "_summary") return;

        if (
          normalize(commodity) === normalize(selectedProduct) ||
          (selectedProduct === "RICE BASMOTI" &&
            normalize(commodity).includes("rice") &&
            normalize(commodity).includes("basmoti"))
        ) {
          Object.entries(commodityData).forEach(([country, values]) => {
            if (country === "_summary") return;
            countryMap[country] =
              (countryMap[country] || 0) +
              parseFloat(values?.value_usd_million || 0);
          });
        }
      });
    });

    return Object.entries(countryMap)
      .map(([name, value]) => ({ name, value: parseFloat(value.toFixed(2)) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [selectedProduct, exportData]);

  // ✅ keep only your original two colors
  const colors = ["#30D5C8", "#FFAA00"];

  if (!selectedProduct || chartData.length === 0)
    return (
      <div
        style={{
          backgroundColor: "#fff",
          padding: 16,
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          marginTop: 24,
        }}
      >
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
          {selectedProduct || "No product selected"} - Top Export Markets
        </h3>
        <div
          style={{
            height: 350,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#888",
          }}
        >
          No data available
        </div>
      </div>
    );

  return (
    <div
      style={{
        backgroundColor: "#fff",
        padding: 16,
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        marginTop: 24,
      }}
    >
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
        {selectedProduct} - Top Export Markets
      </h3>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 24 }}>
        {/* Chart */}
        <div style={{ flex: 1, height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={chartData}
              margin={{ top: 0, right: 30, left: 40, bottom: 0 }}
            >
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 13 }}
                width={120}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                labelStyle={{ fontSize: 13 }}
                contentStyle={{
                  background: "white",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  padding: 8,
                  fontSize: 13,
                }}
              />
              <Bar dataKey="value" barSize={20} radius={[0, 0, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend with country + color box */}
        <div
          style={{
            minWidth: 160,
            background: "#F9F9F9",
            border: "1px solid #eee",
            borderRadius: 8,
            padding: "8px 12px",
            marginTop: 150,
          }}
        >
          <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, }}>
            Legend
          </h4>
          {chartData.map((item, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 6,
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  backgroundColor: colors[idx % colors.length],
                  marginRight: 8,
                }}
              />
              <span style={{ fontSize: 13, color: "#333" }}>{item.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExportDataGraph;
